import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/shared/utils/cn';
import SectionTitle from './SectionTitle';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import {
  type ReelsPresentationVm,
  type SectionItemPresentationVm,
} from '../viewModels/newHome.vm';
import { AppLink } from '@/shared/components/AppLink';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import {
  HOME_PAGE_REEL_CARD_VIEWED,
  HOME_PAGE_REEL_CLICKED,
  HOME_PAGE_REEL_PRODUCT_CLICKED,
  homePageReelCardViewedEventProperties,
  homePageReelClickEventProperties,
} from '../analytics/homePageReels';

const MD_MIN_WIDTH = 768;

function subscribeMdUp(onStoreChange: () => void) {
  const mq = window.matchMedia(`(min-width: ${MD_MIN_WIDTH}px)`);
  mq.addEventListener('change', onStoreChange);
  return () => mq.removeEventListener('change', onStoreChange);
}

function getMdUpSnapshot() {
  return window.matchMedia(`(min-width: ${MD_MIN_WIDTH}px)`).matches;
}

function getMdUpServerSnapshot() {
  return false;
}

function useIsDesktopMd() {
  return useSyncExternalStore(subscribeMdUp, getMdUpSnapshot, getMdUpServerSnapshot);
}

interface ReelsSectionProps {
  data: ReelsPresentationVm;
  sectionKey: string;
  eventLabel: string;
}

/** Passed to `IntersectionObserver` (`useIntersectionObserver` `threshold`). Tune freely. */
const REEL_CARD_VIEWED_IO_THRESHOLDS: number[] = [0, 0.25, 0.5, 0.75, 1];

/** Card must be at least this visible (0–1) to fire `Home Page Reel Card Viewed`. */
const REEL_CARD_VIEWED_MIN_INTERSECTION_RATIO = 0.5;

function ReelCardViewportGate({
  item,
  viewedPositionsRef,
  children,
}: {
  item: SectionItemPresentationVm;
  viewedPositionsRef: MutableRefObject<Set<number>>;
  children: ReactNode;
}) {
  const { analytics, isInitialized } = useAnalytics();
  const position = item.position;

  const analyticsRef = useRef(analytics);
  const isInitRef = useRef(isInitialized);
  const itemRef = useRef(item);

  useLayoutEffect(() => {
    analyticsRef.current = analytics;
    isInitRef.current = isInitialized;
    itemRef.current = item;
  }, [analytics, isInitialized, item]);

  const observerEnabled = isInitialized && Boolean(analytics);

  const onIntersect = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting || entry.intersectionRatio < REEL_CARD_VIEWED_MIN_INTERSECTION_RATIO) {
        return;
      }
      if (viewedPositionsRef.current.has(position)) return;
      const a = analyticsRef.current;
      if (!a || !isInitRef.current) return;
      viewedPositionsRef.current.add(position);
      try {
        a.trackEvent(HOME_PAGE_REEL_CARD_VIEWED, homePageReelCardViewedEventProperties(itemRef.current));
      } catch (err) {
        console.error('Error tracking Home Page Reel Card Viewed:', err);
      }
    },
    [position, viewedPositionsRef],
  );

  const { ref } = useIntersectionObserver<HTMLDivElement>(
    { threshold: REEL_CARD_VIEWED_IO_THRESHOLDS, enabled: observerEnabled },
    onIntersect,
  );

  return (
    <div ref={ref} className="min-h-0 min-w-0 h-full w-full">
      {children}
    </div>
  );
}

function ReelProductBar({
  item,
  blankImageUrl,
}: {
  item: SectionItemPresentationVm;
  blankImageUrl: string;
}) {
  const pill = (
    <div
      className={cn(
        'flex items-center gap-3 rounded-full border border-white/55',
        'bg-[rgba(248,244,236,0.90)] py-0 pl-0 pr-3 shadow-[0_6px_28px_rgba(12,42,74,0.14)]',
        'backdrop-blur-xl backdrop-saturate-150 supports-[backdrop-filter]:bg-[rgba(248, 244, 236, 0.74)]',
      )}
    >
      <div className="h-12 w-12 flex-none overflow-hidden rounded-full border-[1px] border-white bg-neutral-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.12)]">
        <img
          src={item.imageUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={(e) => {
            if (blankImageUrl) {
              (e.target as HTMLImageElement).src = blankImageUrl;
            }
          }}
        />
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        <p className="truncate text-[14px] font-bold leading-snug tracking-tight text-[#0c2a4a]">
          {item.title}
        </p>
        {item.categoryName ? (
          <p className="truncate text-[12px] font-normal leading-snug text-[#5c6d82]">
            {item.categoryName}
          </p>
        ) : null}
      </div>
      {item.link ? (
        <span className="flex-none rounded-[10px] bg-[#0c2a4a] px-3 py-1 text-center text-[13px] font-semibold text-white shadow-[0_2px_8px_rgba(12,42,74,0.35)]">
          {item.btn?.trim() ? item.btn : 'Buy'}
        </span>
      ) : null}
    </div>
  );

  if (!item.link) {
    return pill;
  }

  return (
    <AppLink
      to={item.link}
      shouldRefresh={item.shouldRefresh ?? false}
      eventName={HOME_PAGE_REEL_PRODUCT_CLICKED}
      eventProperties={homePageReelClickEventProperties(item)}
      className="pointer-events-auto block w-full no-underline"
    >
      {pill}
    </AppLink>
  );
}

function ReelCard({
  item,
  isActive,
  sectionInView,
  blankImageUrl,
  sectionKey: _sectionKey,
  isDesktop,
  isSequentialMobile,
  onReelEnd,
  imageAdvanceMs,
}: {
  item: SectionItemPresentationVm;
  isActive: boolean;
  sectionInView: boolean;
  blankImageUrl: string;
  sectionKey: string;
  isDesktop: boolean;
  isSequentialMobile: boolean;
  onReelEnd: () => void;
  imageAdvanceMs: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const videoLoop = isDesktop || !isSequentialMobile;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive && sectionInView) {
      video.currentTime = 0;
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [isActive, sectionInView]);

  useEffect(() => {
    if (isDesktop || !isSequentialMobile) return;
    if (item.videoUrl) return;
    if (!isActive || !sectionInView) return;

    imageAdvanceRef.current = setTimeout(() => {
      imageAdvanceRef.current = null;
      onReelEnd();
    }, Math.max(500, imageAdvanceMs));

    return () => {
      if (imageAdvanceRef.current != null) {
        clearTimeout(imageAdvanceRef.current);
        imageAdvanceRef.current = null;
      }
    };
  }, [
    isDesktop,
    isSequentialMobile,
    item.videoUrl,
    isActive,
    sectionInView,
    imageAdvanceMs,
    onReelEnd,
  ]);

  return (
    <div className="relative block aspect-[9/16] w-full overflow-hidden rounded-2xl bg-neutral-100 shadow-sm ring-1 ring-black/5">
      {item.videoUrl ? (
        <video
          ref={videoRef}
          className="absolute inset-0 z-0 h-full w-full object-cover"
          src={item.videoUrl}
          poster={item.posterUrl || blankImageUrl}
          playsInline
          muted
          loop={videoLoop}
          onEnded={isSequentialMobile ? () => onReelEnd() : undefined}
          preload={isActive ? 'auto' : 'metadata'}
          autoPlay
        />
      ) : (
        <img
          src={item.imageUrl}
          alt={item.title}
          className="absolute inset-0 z-0 h-full w-full object-cover"
          onError={(e) => {
            if (blankImageUrl) {
              (e.target as HTMLImageElement).src = blankImageUrl;
            }
          }}
        />
      )}

      {item.link ? (
        <AppLink
          to={item.link}
          shouldRefresh={item.shouldRefresh ?? false}
          eventName={HOME_PAGE_REEL_CLICKED}
          eventProperties={homePageReelClickEventProperties(item)}
          className="absolute inset-0 z-[1] block"
          aria-label={item.title}
        >
          <span className="sr-only">{item.title}</span>
        </AppLink>
      ) : null}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] p-3">
        <ReelProductBar item={item} blankImageUrl={blankImageUrl} />
      </div>
    </div>
  );
}

function MobileReelHero({
  item,
  sectionInView,
  blankImageUrl,
  onReelEnd,
  imageAdvanceMs,
}: {
  item: SectionItemPresentationVm;
  sectionInView: boolean;
  blankImageUrl: string;
  onReelEnd: () => void;
  imageAdvanceMs: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (sectionInView) {
      video.currentTime = 0;
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [item.position, item.videoUrl, sectionInView]);

  useEffect(() => {
    if (item.videoUrl) return;
    if (!sectionInView) return;

    imageAdvanceRef.current = setTimeout(() => {
      imageAdvanceRef.current = null;
      onReelEnd();
    }, Math.max(500, imageAdvanceMs));

    return () => {
      if (imageAdvanceRef.current != null) {
        clearTimeout(imageAdvanceRef.current);
        imageAdvanceRef.current = null;
      }
    };
  }, [item.position, item.videoUrl, sectionInView, imageAdvanceMs, onReelEnd]);

  if (item.videoUrl) {
    return (
      <video
        ref={videoRef}
        key={item.position}
        className="absolute inset-0 h-full w-full object-cover"
        src={item.videoUrl}
        poster={item.imageUrl}
        playsInline
        muted
        loop={false}
        onEnded={() => onReelEnd()}
        preload="auto"
      />
    );
  }

  return (
    <img
      key={item.position}
      src={item.imageUrl}
      alt={item.title}
      className="absolute inset-0 h-full w-full object-cover"
      onError={(e) => {
        if (blankImageUrl) {
          (e.target as HTMLImageElement).src = blankImageUrl;
        }
      }}
    />
  );
}

function ReelsMobileHero({
  data,
  sectionKey: _sectionKey,
  sectionInView,
  viewedPositionsRef,
}: {
  data: ReelsPresentationVm;
  sectionKey: string;
  sectionInView: boolean;
  viewedPositionsRef: MutableRefObject<Set<number>>;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const thumbRowRef = useRef<HTMLDivElement>(null);
  const count = data.items.length;

  const handleAdvance = useCallback(() => {
    if (count <= 0) return;
    setActiveIndex((i) => (i + 1) % count);
  }, [count]);

  const activeItem = data.items[activeIndex] ?? data.items[0];

  /** Center active thumb using only the strip's scrollLeft (avoids scrollIntoView chaining to the page). */
  useLayoutEffect(() => {
    if (!thumbRowRef.current || !sectionInView) return;
    const row = thumbRowRef.current;
    const el = row.querySelector<HTMLElement>(`[data-reel-thumb="${activeIndex}"]`);
    if (!el) return;
    const id = requestAnimationFrame(() => {
      const r = thumbRowRef.current;
      const e = r?.querySelector<HTMLElement>(`[data-reel-thumb="${activeIndex}"]`);
      if (!r || !e) return;
      const rRect = r.getBoundingClientRect();
      const eRect = e.getBoundingClientRect();
      const next =
        r.scrollLeft + (eRect.left - rRect.left + eRect.width / 2) - rRect.width / 2;
      const max = Math.max(0, r.scrollWidth - r.clientWidth);
      r.scrollTo({ left: Math.max(0, Math.min(next, max)), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [activeIndex, sectionInView, count]);

  if (!activeItem) return null;

  const mainMedia = (
    <div
      key={activeItem.position}
      className="absolute inset-0 h-full w-full nh-reel-hero-fade"
    >
      <MobileReelHero
        item={activeItem}
        sectionInView={sectionInView}
        blankImageUrl={data.blankImageUrl}
        onReelEnd={handleAdvance}
        imageAdvanceMs={data.slideInterval}
      />
    </div>
  );

  return (
    <div className="flex w-full flex-col">
      <ReelCardViewportGate item={activeItem} viewedPositionsRef={viewedPositionsRef}>
      <div className="relative aspect-[9/16] w-full shrink-0 overflow-hidden rounded-none bg-black">
        {/*
          Do not wrap <video> inside <a> — invalid / iOS Safari can suppress timeupdate/ended.
          Tap target is a transparent link layer above the media; gradient & thumbs stack above with correct z-index.
        */}
        <div className="absolute inset-0 z-0">{mainMedia}</div>
        {activeItem.link ? (
          <AppLink
            to={activeItem.link}
            shouldRefresh={activeItem.shouldRefresh ?? false}
            eventName={HOME_PAGE_REEL_CLICKED}
            eventProperties={homePageReelClickEventProperties(activeItem)}
            className="absolute inset-0 z-[1] block"
            aria-label={activeItem.title}
          >
            <span className="sr-only">{activeItem.title}</span>
          </AppLink>
        ) : null}

        <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] flex flex-col items-stretch">
          <div
            ref={thumbRowRef}
            className="pointer-events-auto flex min-w-0 w-full max-w-full touch-pan-x justify-start gap-2.5 overflow-x-auto overscroll-x-contain px-4 pt-4 pb-6 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden"
          >
            {data.items.map((item, idx) => (
              <button
                key={item.position}
                type="button"
                data-reel-thumb={idx}
                onClick={() => setActiveIndex(idx)}
                className={cn(
                  'relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl bg-neutral-800 transition-shadow',
                  idx === activeIndex
                    ? 'ring-[1px] ring-white ring-offset-0 ring-offset-black/30'
                    : 'opacity-90 ring-0',
                )}
                aria-label={item.title}
                aria-pressed={idx === activeIndex}
              >
                <img
                  src={item.imageUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    if (data.blankImageUrl) {
                      (e.target as HTMLImageElement).src = data.blankImageUrl;
                    }
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
      </ReelCardViewportGate>
    </div>
  );
}

const noop = () => undefined;

/** Matches `gap-3` / `min-[768px]:gap-4` on the track; desktop reels use md+ (16px). */
const REELS_DESKTOP_GAP_FALLBACK_PX = 16;

function ReelsDesktopBlock({
  data,
  sectionKey,
  sectionInView,
  viewedPositionsRef,
}: {
  data: ReelsPresentationVm;
  sectionKey: string;
  sectionInView: boolean;
  viewedPositionsRef: MutableRefObject<Set<number>>;
}) {
  const [ambientPlayIndex, setAmbientPlayIndex] = useState(0);
  const [hoveredDesktopIndex, setHoveredDesktopIndex] = useState<number | null>(null);
  const [slideWidthPx, setSlideWidthPx] = useState(() => data.itemWidthPx);
  const viewportElRef = useRef<HTMLDivElement | null>(null);
  const trackElRef = useRef<HTMLDivElement | null>(null);

  const span = useMemo(() => {
    const full = Math.max(1, data.desktopVisibleCount);
    return full + data.desktopPeekFraction;
  }, [data.desktopPeekFraction, data.desktopVisibleCount]);

  const emblaOptions = useMemo(
    () => ({
      axis: 'x' as const,
      align: 'start' as const,
      loop: false,
      containScroll: 'trimSnaps' as const,
      dragFree: false,
    }),
    [],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);

  const setViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      viewportElRef.current = node;
      emblaRef(node);
    },
    [emblaRef],
  );

  const updateSlideWidths = useCallback(() => {
    const vp = viewportElRef.current;
    if (!vp) return;
    const vw = vp.clientWidth;
    if (vw < 48) return;

    let gapPx = REELS_DESKTOP_GAP_FALLBACK_PX;
    const track = trackElRef.current;
    if (track && typeof getComputedStyle !== 'undefined') {
      const parsed = parseFloat(getComputedStyle(track).columnGap || '0');
      if (Number.isFinite(parsed) && parsed > 0) {
        gapPx = parsed;
      }
    }

    const gapCount = Math.max(0, Math.ceil(span - 1e-6) - 1);
    const raw = (vw - gapPx * gapCount) / span;
    const next = Math.round(Math.min(Math.max(200, raw), 640));
    setSlideWidthPx((prev) => (Math.abs(prev - next) < 1 ? prev : next));
  }, [span]);

  const pickRandomFromViewport = useCallback(() => {
    if (!emblaApi || !sectionInView || hoveredDesktopIndex !== null) return;
    const inView = emblaApi.slidesInView();
    if (inView.length === 0) return;
    const choice = inView[Math.floor(Math.random() * inView.length)]!;
    setAmbientPlayIndex(choice);
  }, [emblaApi, hoveredDesktopIndex, sectionInView]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSettle = () => {
      pickRandomFromViewport();
    };

    emblaApi.on('init', onSettle);
    emblaApi.on('settle', onSettle);
    emblaApi.on('reInit', onSettle);

    return () => {
      emblaApi.off('init', onSettle);
      emblaApi.off('settle', onSettle);
      emblaApi.off('reInit', onSettle);
    };
  }, [emblaApi, pickRandomFromViewport]);

  useEffect(() => {
    if (!sectionInView || hoveredDesktopIndex !== null) return;
    queueMicrotask(() => {
      pickRandomFromViewport();
    });
  }, [hoveredDesktopIndex, pickRandomFromViewport, sectionInView]);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      updateSlideWidths();
    });
    return () => cancelAnimationFrame(id);
  }, [updateSlideWidths, data.items.length]);

  useLayoutEffect(() => {
    const el = viewportElRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      updateSlideWidths();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [updateSlideWidths]);

  useEffect(() => {
    if (!emblaApi) return;
    const id = requestAnimationFrame(() => {
      emblaApi.reInit();
    });
    return () => cancelAnimationFrame(id);
  }, [emblaApi, slideWidthPx]);

  const playingIndex =
    !sectionInView ? -1 : (hoveredDesktopIndex ?? ambientPlayIndex);

  return (
    <div className="w-full px-4 pb-10 pt-1 min-[768px]:px-6 min-[768px]:pb-14">
      <div className="overflow-hidden" ref={setViewportRef}>
        <div ref={trackElRef} className="flex touch-pan-y gap-3 min-[768px]:gap-4">
          {data.items.map((item, idx) => (
            <div
              key={item.position}
              className="min-w-0 shrink-0 grow-0"
              style={{
                flex: `0 0 ${slideWidthPx}px`,
                width: slideWidthPx,
              }}
              onMouseEnter={() => {
                setHoveredDesktopIndex(idx);
              }}
              onMouseLeave={() => {
                setHoveredDesktopIndex(null);
              }}
            >
              <ReelCardViewportGate item={item} viewedPositionsRef={viewedPositionsRef}>
                <ReelCard
                  item={item}
                  isActive={playingIndex === idx}
                  sectionInView={sectionInView}
                  blankImageUrl={data.blankImageUrl}
                  sectionKey={sectionKey}
                  isDesktop
                  isSequentialMobile={false}
                  onReelEnd={noop}
                  imageAdvanceMs={data.slideInterval}
                />
              </ReelCardViewportGate>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ReelsSection({ data, sectionKey, eventLabel }: ReelsSectionProps) {
  const isDesktop = useIsDesktopMd();
  const viewedReelPositionsRef = useRef<Set<number>>(new Set());

  const { ref: sectionRef, isIntersecting: sectionInView } = useIntersectionObserver<HTMLElement>({
    threshold: 0.1,
  });

  return (
    <section
      ref={sectionRef}
      className={cn(
        'relative w-full',
        !isDesktop && 'flex flex-col',
        isDesktop && 'min-[768px]:min-h-0',
      )}
      style={{ backgroundColor: data.bgColor, color: data.textColor }}
      data-analytics-section-name={eventLabel}
    >
      {data.title ? (
        <SectionTitle title={data.title} variant="category" />
      ) : null}
      {isDesktop ? (
        <ReelsDesktopBlock
          data={data}
          sectionKey={sectionKey}
          sectionInView={sectionInView}
          viewedPositionsRef={viewedReelPositionsRef}
        />
      ) : (
        <div className="w-full pb-4 pt-1">
          <ReelsMobileHero
            data={data}
            sectionKey={sectionKey}
            sectionInView={sectionInView}
            viewedPositionsRef={viewedReelPositionsRef}
          />
        </div>
      )}
    </section>
  );
}
